// LOOP CUSTOMER DOCUMENT ID INVARIANT V20 — Firestore document id always wins over stale/null stored id fields.
/**
 * src/data/firestoreClient.js
 *
 * Single Firestore access layer:
 * - iOS (Capacitor WKWebView): uses Firestore REST via CapacitorHttp (URLSession)
 *   to bypass status=0 / "Load failed" issues when hitting firestore.googleapis.com from WebView.
 * - Android/Web: uses Firebase Web SDK (getDoc/getDocs/addDoc/updateDoc).
 *
 * This file is intended to be the ONLY place that talks to Firestore directly.
 *
 * Usage:
 *   import fsClient from "./data/firestoreClient";
 *
 *   const biz = await fsClient.getDocByPath(`businesses/${id}`);
 *   const services = await fsClient.listCollectionByPath(`businesses/${id}/services`);
 *   await fsClient.addDocToCollectionPath(`businesses/${id}/bookings`, { ... });
 *
 * Notes:
 * - For iOS REST calls, an idToken is required for protected reads/writes.
 *   We read it from localStorage "__MANUAL_AUTH__" (set by Login fallback).
 */

import { isIosCapacitor, httpRequestJson } from "../utils/nativeHttp";
import { getManualAuth, isManualAuthLikelyValid, persistManualAuth } from "../utils/manualAuth";
import { isFirebaseNativeAvailable, refreshSession } from "../native/firebaseNative";
import { firebaseConfigRuntime, db as defaultDb } from "../firebase";

// Web SDK (used only on non-iOS)
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  query as fsQuery,
  where as fsWhere,
  orderBy as fsOrderBy,
  limit as fsLimit,
} from "firebase/firestore";

/* ------------------------- auth token helpers -------------------------- */

function normalizeNativeSessionForManualAuth(result, previous = null) {
  const source = previous?.source || "ios-native-refresh";
  const idToken = result?.idToken || result?.token || result?.user?.idToken || "";
  if (!idToken) return null;

  return {
    ...(previous || {}),
    ...(result || {}),
    idToken: String(idToken),
    refreshToken: result?.refreshToken || previous?.refreshToken || null,
    expiresIn: String(result?.expiresIn || result?.expirationTime || previous?.expiresIn || "3600"),
    uid: result?.uid || result?.user?.uid || previous?.uid || null,
    email: result?.email || result?.user?.email || previous?.email || "",
    displayName: result?.displayName || result?.name || result?.user?.displayName || previous?.displayName || previous?.name || "",
    obtainedAt: new Date().toISOString(),
    source,
  };
}

async function getIdTokenForRest() {
  let ma = getManualAuth();

  if (ma && ma.idToken && isManualAuthLikelyValid(ma)) {
    return ma.idToken;
  }

  // iOS native sessions expire. Before hitting Firestore REST with a stale token,
  // ask the native Firebase bridge for a fresh Firebase ID token when available.
  try {
    if (isIosCapacitor() && isFirebaseNativeAvailable()) {
      const refreshed = await refreshSession();
      const next = normalizeNativeSessionForManualAuth(refreshed, ma);
      if (next && next.idToken) {
        persistManualAuth(next);
        return next.idToken;
      }
    }
  } catch (err) {
    try { console.warn("[firestoreClient] native refreshSession failed", err); } catch (_) {}
  }

  ma = getManualAuth();
  if (ma && ma.idToken) return ma.idToken;
  return null;
}

/* ------------------------ Firestore REST helpers ----------------------- */

function getProjectId() {
  const fromRuntime = firebaseConfigRuntime && firebaseConfigRuntime.projectId ? firebaseConfigRuntime.projectId : null;
  if (fromRuntime) return fromRuntime;
  try {
    const w = typeof window !== "undefined" ? window.__FIREBASE_CONFIG__ : null;
    if (w && w.projectId) return w.projectId;
  } catch (_) {}
  return null;
}

function firestoreRestBase() {
  const projectId = getProjectId();
  if (!projectId) throw new Error("firestoreClient: missing firebase projectId");
  return `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents`;
}

function encodePath(path) {
  // Firestore REST wants segments separated by '/'
  // We'll just trim redundant slashes
  return String(path || "").replace(/^\/+/, "").replace(/\/+$/, "");
}


function buildUpdateMaskParams(fieldPaths = []) {
  const clean = Array.from(new Set((fieldPaths || []).map((x) => String(x || "").trim()).filter(Boolean)));
  const query = clean
    .map((field) => `updateMask.fieldPaths=${encodeURIComponent(field)}`)
    .join("&");
  return { clean, query };
}

function appendQuery(url, query) {
  if (!query) return url;
  return `${url}${url.includes("?") ? "&" : "?"}${query}`;
}


/**
 * Converts a JS value into Firestore REST Value JSON.
 * Minimal but practical (strings, numbers, booleans, null, arrays, objects, Date).
 */
function toFsValue(v) {
  if (v === null || v === undefined) return { nullValue: null };

  if (v instanceof Date) return { timestampValue: v.toISOString() };

  const t = typeof v;
  if (t === "string") return { stringValue: v };
  if (t === "boolean") return { booleanValue: v };

  if (t === "number") {
    // Firestore REST supports integerValue as string, doubleValue as number
    if (Number.isInteger(v)) return { integerValue: String(v) };
    return { doubleValue: v };
  }

  if (Array.isArray(v)) {
    return { arrayValue: { values: v.map((x) => toFsValue(x)) } };
  }

  if (t === "object") {
    const fields = {};
    for (const [k, val] of Object.entries(v)) {
      fields[k] = toFsValue(val);
    }
    return { mapValue: { fields } };
  }

  // fallback
  return { stringValue: String(v) };
}

function fromFsValue(val) {
  if (!val || typeof val !== "object") return null;

  if (Object.prototype.hasOwnProperty.call(val, "nullValue")) return null;
  if (Object.prototype.hasOwnProperty.call(val, "stringValue")) return val.stringValue;
  if (Object.prototype.hasOwnProperty.call(val, "booleanValue")) return Boolean(val.booleanValue);

  if (Object.prototype.hasOwnProperty.call(val, "integerValue")) {
    const n = Number(val.integerValue);
    return Number.isNaN(n) ? val.integerValue : n;
  }
  if (Object.prototype.hasOwnProperty.call(val, "doubleValue")) return Number(val.doubleValue);

  if (Object.prototype.hasOwnProperty.call(val, "timestampValue")) {
    // keep ISO string (easy for JS)
    return val.timestampValue;
  }

  if (Object.prototype.hasOwnProperty.call(val, "mapValue")) {
    const fields = (val.mapValue && val.mapValue.fields) ? val.mapValue.fields : {};
    const out = {};
    for (const [k, v] of Object.entries(fields)) out[k] = fromFsValue(v);
    return out;
  }

  if (Object.prototype.hasOwnProperty.call(val, "arrayValue")) {
    const values = (val.arrayValue && Array.isArray(val.arrayValue.values)) ? val.arrayValue.values : [];
    return values.map(fromFsValue);
  }

  // GeoPoint / Reference types can appear as "geoPointValue"/"referenceValue"
  if (Object.prototype.hasOwnProperty.call(val, "geoPointValue")) return val.geoPointValue;
  if (Object.prototype.hasOwnProperty.call(val, "referenceValue")) return val.referenceValue;

  return null;
}

function parseRestDoc(docJson) {
  if (!docJson) return null;
  const name = docJson.name || "";
  const parts = String(name).split("/").filter(Boolean);
  const id = parts.length ? parts[parts.length - 1] : null;
  const fields = docJson.fields || {};
  const out = {};
  for (const [k, v] of Object.entries(fields)) out[k] = fromFsValue(v);

  // V20: a stored field such as `id: null` must never overwrite the real
  // Firestore document id. Keep the canonical document id as the final value.
  if (id) {
    out.__documentId = id;
    out.id = id;
  }
  return out;
}

/**
 * Build REST "runQuery" structuredQuery from simple opts.
 * Supported: where=[{field, op, value}], orderBy=[{field, direction}], limit
 *
 * ops allowed: "EQUAL", "ARRAY_CONTAINS"
 * direction: "ASCENDING" | "DESCENDING"
 */
function buildStructuredQuery(collectionId, opts = {}) {
  const whereArr = Array.isArray(opts.where) ? opts.where : [];
  const orderArr = Array.isArray(opts.orderBy) ? opts.orderBy : [];
  const lim = opts.limit != null ? Number(opts.limit) : null;

  const structured = {
    from: [{ collectionId }],
  };

  if (whereArr.length > 0) {
    // Only AND of single-field filters (most common). If multiple, we use compositeFilter AND.
    const filters = whereArr
      .filter((w) => w && w.field && w.op)
      .map((w) => ({
        fieldFilter: {
          field: { fieldPath: String(w.field) },
          op: String(w.op),
          value: toFsValue(w.value),
        },
      }));

    if (filters.length === 1) structured.where = filters[0];
    else if (filters.length > 1) structured.where = { compositeFilter: { op: "AND", filters } };
  }

  if (orderArr.length > 0) {
    structured.orderBy = orderArr
      .filter((o) => o && o.field)
      .map((o) => ({
        field: { fieldPath: String(o.field) },
        direction: (o.direction || "ASCENDING"),
      }));
  }

  if (Number.isFinite(lim) && lim > 0) structured.limit = lim;

  return structured;
}

async function restGetDocByPath(path) {
  const token = await getIdTokenForRest();
  const base = firestoreRestBase();
  const fullPath = encodePath(path);
  const url = `${base}/${fullPath}`;

  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const json = await httpRequestJson({ url, method: "GET", headers });
  return parseRestDoc(json);
}

async function restListCollectionByPath(path, opts = {}) {
  // If opts empty, use "list documents": GET /{collectionPath}?pageSize=...
  // If opts has where/orderBy, use runQuery.
  const token = await getIdTokenForRest();
  const base = firestoreRestBase();
  const fullPath = encodePath(path);
  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const hasQuery = (opts && (Array.isArray(opts.where) && opts.where.length > 0)) ||
                   (opts && (Array.isArray(opts.orderBy) && opts.orderBy.length > 0));

  if (!hasQuery) {
    const url = `${base}/${fullPath}`;
    const params = {};
    if (opts && opts.limit) params.pageSize = String(opts.limit);
    const json = await httpRequestJson({ url, method: "GET", headers, params });
    const docs = (json && Array.isArray(json.documents)) ? json.documents.map(parseRestDoc).filter(Boolean) : [];
    return docs;
  }

  // runQuery endpoint requires parent document path (not including collectionId at end) for subcollections,
  // but supports running on root too. We'll set parent to base + (parentPath or "").
  const segments = fullPath.split("/").filter(Boolean);
  const collectionId = segments[segments.length - 1];
  const parentPath = segments.slice(0, -1).join("/"); // may be empty for root collection
  const parentUrl = parentPath ? `${base}/${parentPath}` : base;

  const url = `${parentUrl}:runQuery`;
  const body = {
    structuredQuery: buildStructuredQuery(collectionId, opts),
  };

  const res = await httpRequestJson({ url, method: "POST", headers, data: body });

  // runQuery returns array of {document:{...}} entries
  const docs = Array.isArray(res)
    ? res.map((row) => (row && row.document ? parseRestDoc(row.document) : null)).filter(Boolean)
    : [];
  return docs;
}

async function restAddDocToCollectionPath(path, data) {
  const token = await getIdTokenForRest();
  const base = firestoreRestBase();
  const fullPath = encodePath(path);
  const url = `${base}/${fullPath}`;

  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const fields = {};
  const obj = data && typeof data === "object" ? data : {};
  for (const [k, v] of Object.entries(obj)) fields[k] = toFsValue(v);

  const json = await httpRequestJson({
    url,
    method: "POST",
    headers,
    data: { fields },
  });

  return parseRestDoc(json);
}

async function restSetDocByPath(path, data, options = {}) {
  const token = await getIdTokenForRest();
  const base = firestoreRestBase();
  const fullPath = encodePath(path);
  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const fields = {};
  const obj = data && typeof data === "object" ? data : {};
  const fieldPaths = [];
  for (const [k, v] of Object.entries(obj)) {
    fields[k] = toFsValue(v);
    fieldPaths.push(k);
  }

  // setDoc(..., { merge:true }) behavior for REST.
  // Without updateMask, PATCH can behave like an overwrite in REST and can also
  // make rules evaluate a larger/different write than intended.
  const shouldMerge = options?.merge !== false;
  let url = `${base}/${fullPath}`;
  if (shouldMerge && fieldPaths.length) {
    const { query } = buildUpdateMaskParams(fieldPaths);
    url = appendQuery(url, query);
  }

  const json = await httpRequestJson({ url, method: "PATCH", headers, data: { fields } });
  return parseRestDoc(json);
}

async function restUpdateDocByPath(path, data) {
  const token = await getIdTokenForRest();
  const base = firestoreRestBase();
  const fullPath = encodePath(path);

  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const fields = {};
  const obj = data && typeof data === "object" ? data : {};
  const updateMask = [];
  for (const [k, v] of Object.entries(obj)) {
    fields[k] = toFsValue(v);
    updateMask.push(k);
  }

  const { query } = buildUpdateMaskParams(updateMask);
  const url = appendQuery(`${base}/${fullPath}`, query);

  const json = await httpRequestJson({
    url,
    method: "PATCH",
    headers,
    data: { fields },
  });

  return parseRestDoc(json);
}

/* ------------------------ Web SDK helpers (non-iOS) -------------------- */

async function sdkGetDocByPath(path, db = defaultDb) {
  const fullPath = encodePath(path);
  const segments = fullPath.split("/").filter(Boolean);
  if (segments.length % 2 !== 0) throw new Error(`getDocByPath expects a document path, got: ${path}`);
  const ref = doc(db, ...segments);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { ...(snap.data() || {}), __documentId: snap.id, id: snap.id };
}

async function sdkListCollectionByPath(path, opts = {}, db = defaultDb) {
  const fullPath = encodePath(path);
  const segments = fullPath.split("/").filter(Boolean);
  if (segments.length % 2 === 0) throw new Error(`listCollectionByPath expects a collection path, got: ${path}`);
  let qRef = collection(db, ...segments);

  let qBuilt = qRef;
  const whereArr = Array.isArray(opts.where) ? opts.where : [];
  const orderArr = Array.isArray(opts.orderBy) ? opts.orderBy : [];

  if (whereArr.length > 0) {
    for (const w of whereArr) {
      if (!w || !w.field || !w.op) continue;
      // map REST ops to SDK ops
      const opMap = { EQUAL: "==", ARRAY_CONTAINS: "array-contains" };
      const sdkOp = opMap[String(w.op)] || w.op;
      qBuilt = fsQuery(qBuilt, fsWhere(String(w.field), sdkOp, w.value));
    }
  }

  if (orderArr.length > 0) {
    for (const o of orderArr) {
      if (!o || !o.field) continue;
      const dir = (o.direction || "ASCENDING") === "DESCENDING" ? "desc" : "asc";
      qBuilt = fsQuery(qBuilt, fsOrderBy(String(o.field), dir));
    }
  }

  if (opts && opts.limit) qBuilt = fsQuery(qBuilt, fsLimit(Number(opts.limit)));

  const snap = await getDocs(qBuilt);
  return snap.docs.map((d) => ({ ...(d.data() || {}), __documentId: d.id, id: d.id }));
}

async function sdkAddDocToCollectionPath(path, data, db = defaultDb) {
  const fullPath = encodePath(path);
  const segments = fullPath.split("/").filter(Boolean);
  if (segments.length % 2 === 0) throw new Error(`addDocToCollectionPath expects a collection path, got: ${path}`);
  const colRef = collection(db, ...segments);
  const ref = await addDoc(colRef, data);
  return { id: ref.id };
}

async function sdkSetDocByPath(path, data, db = defaultDb) {
  const fullPath = encodePath(path);
  const segments = fullPath.split("/").filter(Boolean);
  if (segments.length % 2 !== 0) throw new Error(`setDocByPath expects a document path, got: ${path}`);
  const ref = doc(db, ...segments);
  await setDoc(ref, data, { merge: true });
  return { ok: true };
}

async function sdkUpdateDocByPath(path, data, db = defaultDb) {
  const fullPath = encodePath(path);
  const segments = fullPath.split("/").filter(Boolean);
  if (segments.length % 2 !== 0) throw new Error(`updateDocByPath expects a document path, got: ${path}`);
  const ref = doc(db, ...segments);
  await updateDoc(ref, data);
  return { ok: true };
}

/* ------------------------------ Public API ----------------------------- */

const firestoreClient = {
  async setFavorite(uid, businessId, added, { db } = {}) {
    if (!uid || !businessId || String(uid).includes("/") || typeof added !== "boolean") {
      throw new Error("Invalid favorite mutation");
    }
    const id = String(businessId);
    if (!isIosCapacitor()) {
      return updateDoc(doc(db || defaultDb, "users", uid), {
        favorites: added ? arrayUnion(id) : arrayRemove(id),
      });
    }
    const token = await getIdTokenForRest();
    if (!token) throw new Error("Sign in to save favorites");
    const base = firestoreRestBase();
    return httpRequestJson({
      url: `${base}:commit`, method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data: { writes: [{
        transform: {
          document: `projects/${getProjectId()}/databases/(default)/documents/users/${uid}`,
          fieldTransforms: [{ fieldPath: "favorites", [added ? "appendMissingElements" : "removeAllFromArray"]: {
            values: [{ stringValue: id }],
          } }],
        },
        currentDocument: { exists: true },
      }] },
    });
  },
  /**
   * getDocByPath("users/uid")
   * -> { id, ...data } or null
   */
  async getDocByPath(path, { db } = {}) {
    if (isIosCapacitor()) return restGetDocByPath(path);
    return sdkGetDocByPath(path, db || defaultDb);
  },

  /**
   * listCollectionByPath("businesses/123/services", { where, orderBy, limit })
   * -> array of { id, ...data }
   */
  async listCollectionByPath(path, opts = {}, { db } = {}) {
    if (isIosCapacitor()) return restListCollectionByPath(path, opts);
    return sdkListCollectionByPath(path, opts, db || defaultDb);
  },

  /**
   * addDocToCollectionPath("businesses/123/bookings", data)
   * -> { id }
   */
  async addDocToCollectionPath(path, data, { db } = {}) {
    if (isIosCapacitor()) return restAddDocToCollectionPath(path, data);
    return sdkAddDocToCollectionPath(path, data, db || defaultDb);
  },

  /**
   * setDocByPath("users/uid/legalAcceptances/docId", data)
   * -> merge/set document (iOS REST safe)
   */
  async setDocByPath(path, data, options = {}) {
    const { db, merge = true } = options || {};
    if (isIosCapacitor()) return restSetDocByPath(path, data, { merge });
    return sdkSetDocByPath(path, data, db || defaultDb);
  },

  /**
   * updateDocByPath("users/uid", partialData)
   * -> { ok: true } (sdk) or updated doc (rest)
   */
  async updateDocByPath(path, data, { db } = {}) {
    if (isIosCapacitor()) return restUpdateDocByPath(path, data);
    return sdkUpdateDocByPath(path, data, db || defaultDb);
  },
};

export default firestoreClient;
